import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { FiMenu, FiX, FiExternalLink, FiSun, FiMoon } from "react-icons/fi";
import { FaGithub, FaLinkedin, FaRobot } from "react-icons/fa";
import { navbarVariant, fadeInDown, staggerContainer, fadeIn } from "../../animations/animations";
import "./navbar.css";

function Navbar({ currentPage, isAIChatOpen, openAIChat, theme, toggleTheme }) {
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeItem, setActiveItem] = useState(currentPage);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setActiveItem(currentPage);
  }, [currentPage]);

  const handleHomeRoute = () => {
    navigate("/");
    setMobileOpen(false);
  };

  const handleDeveloperRoute = () => {
    navigate("/developer");
    setMobileOpen(false);
  };

  const handleWebRemoteControlRoute = () => {
    window.open("/web-remote-control", "_blank");
    setMobileOpen(false);
  };

  const handleApiClick = () => {
    navigate("/api-tester")
    setMobileOpen(false);
  };

  const handleMcpBlogClick = () => {
    navigate("/mcp-blog");
    setMobileOpen(false);
  };

  const handleAiChat = () => {
    if (!isAIChatOpen && openAIChat) openAIChat();
    setMobileOpen(false);
  };

  const navLinks = [
    { id: "home", label: "Home", action: handleHomeRoute },
    { id: "developer", label: "Developer", action: handleDeveloperRoute },
    { id: "mcp", label: "MCP Blog", action: handleMcpBlogClick },
    { id: "api", label: "API Tester", action: handleApiClick }
  ];

  const isDark = theme === "dark";

  return (
    <>
      <motion.nav
        className={`premium-navbar ${scrolled ? "navbar-scrolled" : ""}`}
        variants={navbarVariant}
        initial="hidden"
        animate="visible"
      >
        <div className="navbar-inner">
          {/* Logo */}
          <motion.div
            className="navbar-logo"
            onClick={handleHomeRoute}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.97 }}
          >
            <span className="logo-bracket">&lt;</span>
            <span className="logo-text">ShabinSV</span>
            <span className="logo-bracket">/&gt;</span>
            <motion.div
              className="logo-glow"
              animate={{
                opacity: [0.4, 0.9, 0.4],
                scale: [1, 1.2, 1],
              }}
              transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut" }}
            />
          </motion.div>

          {/* Desktop nav links */}
          <motion.ul
            className="navbar-links"
            variants={staggerContainer}
            initial="hidden"
            animate="visible"
          >
            {navLinks.map((link) => (
              <motion.li key={link.id} variants={fadeIn}>
                <button
                  className={`nav-link-btn ${activeItem === link.id ? "nav-link-active" : ""}`}
                  onClick={link.action}
                >
                  {link.label}
                  <span className="nav-underline" />
                </button>
              </motion.li>
            ))}

            {/* AI Chat */}
            <motion.li variants={fadeIn}>
              <motion.button
                className={`nav-ai-btn ${isAIChatOpen ? "nav-ai-disabled" : ""}`}
                onClick={handleAiChat}
                whileHover={!isAIChatOpen ? { scale: 1.05, boxShadow: "0 0 25px rgba(79,172,254,0.5)" } : {}}
                whileTap={!isAIChatOpen ? { scale: 0.95 } : {}}
                disabled={isAIChatOpen}
              >
                <motion.span
                  animate={{ y: [0, -2, 0] }}
                  transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
                >
                  <FaRobot />
                </motion.span>
                AI Chat
              </motion.button>
            </motion.li>

            {/* Web Remote Control CTA */}
            <motion.li variants={fadeIn}>
              <motion.button
                className="nav-cta-btn"
                onClick={handleWebRemoteControlRoute}
                whileHover={{ scale: 1.05, boxShadow: "0 0 30px rgba(99,102,241,0.5)" }}
                whileTap={{ scale: 0.95 }}
              >
                Remote Control
                <FiExternalLink size={13} style={{ marginLeft: 5 }} />
              </motion.button>
            </motion.li>
          </motion.ul>

          {/* Right side: socials + theme toggle */}
          <div className="navbar-right">
            {/* Social icons desktop */}
            <div className="navbar-socials">
              <motion.a
                href="https://in.linkedin.com/in/shabin-sv-831872228"
                target="_blank"
                rel="noreferrer"
                className="social-icon"
                whileHover={{ scale: 1.2, color: "#0ea5e9" }}
              >
                <FaLinkedin />
              </motion.a>
              <motion.a
                href="https://github.com/shabinsv"
                target="_blank"
                rel="noreferrer"
                className="social-icon"
                whileHover={{ scale: 1.2, color: "#a78bfa" }}
              >
                <FaGithub />
              </motion.a>
            </div>

            {/* Theme toggle */}
            {toggleTheme && (
              <motion.button
                className="theme-toggle-btn"
                onClick={toggleTheme}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9, rotate: 15 }}
                title={isDark ? "Switch to light mode" : "Switch to dark mode"}
              >
                <AnimatePresence mode="wait" initial={false}>
                  <motion.span
                    key={isDark ? "sun" : "moon"}
                    initial={{ rotate: -30, opacity: 0, scale: 0.7 }}
                    animate={{ rotate: 0, opacity: 1, scale: 1 }}
                    exit={{ rotate: 30, opacity: 0, scale: 0.7 }}
                    transition={{ duration: 0.25 }}
                  >
                    {isDark ? <FiSun size={16} /> : <FiMoon size={16} />}
                  </motion.span>
                </AnimatePresence>
              </motion.button>
            )}
          </div>

          {/* Mobile hamburger */}
          <motion.button
            className="navbar-hamburger"
            onClick={() => setMobileOpen(!mobileOpen)}
            whileTap={{ scale: 0.9 }}
            aria-label="Toggle menu"
          >
            {mobileOpen ? <FiX size={22} /> : <FiMenu size={22} />}
          </motion.button>
        </div>
      </motion.nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            className="mobile-menu"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
          >
            <motion.ul
              variants={staggerContainer}
              initial="hidden"
              animate="visible"
            >
              {navLinks.map((link) => (
                <motion.li key={link.id} variants={fadeInDown}>
                  <button
                    className={`mobile-nav-link ${activeItem === link.id ? "mobile-nav-active" : ""}`}
                    onClick={link.action}
                  >
                    {link.label}
                  </button>
                </motion.li>
              ))}
              <motion.li variants={fadeInDown}>
                <button
                  className={`mobile-nav-link mobile-ai-btn ${isAIChatOpen ? "mobile-ai-disabled" : ""}`}
                  onClick={handleAiChat}
                  disabled={isAIChatOpen}
                >
                  <FaRobot style={{ marginRight: 8 }} /> AI Chat
                </button>
              </motion.li>
              <motion.li variants={fadeInDown}>
                <button
                  className="mobile-nav-link mobile-cta"
                  onClick={handleWebRemoteControlRoute}
                >
                  Remote Control <FiExternalLink size={12} style={{ marginLeft: 6 }} />
                </button>
              </motion.li>
              {/* Theme toggle in mobile */}
              {toggleTheme && (
                <motion.li variants={fadeInDown}>
                  <button
                    className="mobile-nav-link mobile-theme-toggle"
                    onClick={() => { toggleTheme(); setMobileOpen(false); }}
                  >
                    {isDark ? <FiSun size={14} style={{ marginRight: 8 }} /> : <FiMoon size={14} style={{ marginRight: 8 }} />}
                    {isDark ? "Light Mode" : "Dark Mode"}
                  </button>
                </motion.li>
              )}
              <motion.li variants={fadeInDown}>
                <div className="mobile-socials">
                  <a href="https://in.linkedin.com/in/shabin-sv-831872228" target="_blank" rel="noreferrer" className="social-icon">
                    <FaLinkedin />
                  </a>
                  <a href="https://github.com/shabinsv" target="_blank" rel="noreferrer" className="social-icon">
                    <FaGithub />
                  </a>
                </div>
              </motion.li>
            </motion.ul>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

export default Navbar;
